import isEmpty from 'lodash/isEmpty'
import type {
  BatchAssignChatsArgs,
  BatchCancelChatsArgs,
  BatchCloseChatsArgs,
  BatchMarkChatsAsSpamArgs,
  BatchReplyToChatsArgs,
  BatchUpdateChatRemarksArgs,
  BatchUpdateChatTagsArgs,
  ChatBatchReplyInput,
  ChatBatchResult,
} from '@ztechapps/common.contract'
import { useActionMutation, useError, useToast } from '@ztechapps/common.hook'
import {
  batchAssignChatsMutation,
  batchCancelChatsMutation,
  batchCloseChatsMutation,
  batchMarkChatsAsSpamMutation,
  batchReplyToChatsMutation,
  batchUpdateChatRemarksMutation,
  batchUpdateChatTagsMutation,
} from './graphql'
import { useConversationInboxContext } from './Context'
import { reportBatchOutcome } from './reportBatchOutcome'

export type UseConversationBatchActionsResult = {
  closeConversations: (chatIds: string[]) => Promise<void>
  cancelConversations: (chatIds: string[]) => Promise<void>
  markSpamConversations: (chatIds: string[]) => Promise<void>
  assignConversations: (chatIds: string[], assigneeId?: string) => Promise<void>
  updateTags: (chatIds: string[], tags: string[]) => Promise<void>
  updateRemarks: (chatIds: string[], remarks: string) => Promise<void>
  replyToConversations: (replies: ChatBatchReplyInput[]) => Promise<void>
  closing: boolean
  canceling: boolean
  markingSpam: boolean
  assigning: boolean
  updatingTags: boolean
  updatingRemarks: boolean
  replying: boolean
}

export const useConversationBatchActions = (): UseConversationBatchActionsResult => {
  const { chatList, chatListWithSelection, onRefresh } = useConversationInboxContext()
  const { showSuccess, showError } = useToast()

  const [closeExecute, { loading: closing, error: closeError }] = useActionMutation<
    ChatBatchResult,
    BatchCloseChatsArgs
  >({
    query: batchCloseChatsMutation,
  })
  const [cancelExecute, { loading: canceling, error: cancelError }] = useActionMutation<
    ChatBatchResult,
    BatchCancelChatsArgs
  >({ query: batchCancelChatsMutation })
  const [markSpamExecute, { loading: markingSpam, error: markSpamError }] = useActionMutation<
    ChatBatchResult,
    BatchMarkChatsAsSpamArgs
  >({ query: batchMarkChatsAsSpamMutation })
  const [assignExecute, { loading: assigning, error: assignError }] = useActionMutation<
    ChatBatchResult,
    BatchAssignChatsArgs
  >({ query: batchAssignChatsMutation })
  const [updateTagsExecute, { loading: updatingTags, error: updateTagsError }] = useActionMutation<
    ChatBatchResult,
    BatchUpdateChatTagsArgs
  >({ query: batchUpdateChatTagsMutation })
  const [updateRemarksExecute, { loading: updatingRemarks, error: updateRemarksError }] = useActionMutation<
    ChatBatchResult,
    BatchUpdateChatRemarksArgs
  >({ query: batchUpdateChatRemarksMutation })
  const [replyExecute, { loading: replying, error: replyError }] = useActionMutation<
    ChatBatchResult,
    BatchReplyToChatsArgs
  >({ query: batchReplyToChatsMutation })

  useError(closeError)
  useError(cancelError)
  useError(markSpamError)
  useError(assignError)
  useError(updateTagsError)
  useError(updateRemarksError)
  useError(replyError)

  const reportOutcome = (noun: string, verb: string, result: ChatBatchResult) =>
    reportBatchOutcome({ showSuccess, showError }, noun, verb, result)

  // The selection hook only clears as a side effect of the refetched list changing identity,
  // so clear it explicitly from the currently loaded rows — the refetch below then lands the
  // fresh rows on an already-cleared selection either way.
  const clearSelection = () => {
    chatListWithSelection.reset(chatList.list ?? [])
  }

  const closeConversations = async (chatIds: string[]) => {
    try {
      const result = await closeExecute({ chatIds })
      if (!isEmpty(result)) reportOutcome('conversation', 'closed', result)
    } finally {
      clearSelection()
      await onRefresh()
    }
  }

  const cancelConversations = async (chatIds: string[]) => {
    try {
      const result = await cancelExecute({ chatIds })
      if (!isEmpty(result)) reportOutcome('conversation', 'cancelled', result)
    } finally {
      clearSelection()
      await onRefresh()
    }
  }

  // Marking is reversible, so it runs with no confirmation — the shared runner still isolates each
  // conversation's failure and the list still refreshes either way.
  const markSpamConversations = async (chatIds: string[]) => {
    try {
      const result = await markSpamExecute({ chatIds })
      if (!isEmpty(result)) reportOutcome('conversation', 'marked as spam', result)
    } finally {
      clearSelection()
      await onRefresh()
    }
  }

  const assignConversations = async (chatIds: string[], assigneeId?: string) => {
    try {
      const result = await assignExecute({ chatIds, assigneeId: assigneeId ?? null })
      if (!isEmpty(result)) reportOutcome('conversation', 'assigned', result)
    } finally {
      clearSelection()
      await onRefresh()
    }
  }

  const updateTags = async (chatIds: string[], tags: string[]) => {
    try {
      const result = await updateTagsExecute({ chatIds, tags })
      if (!isEmpty(result)) reportOutcome('conversation', 'updated (tags)', result)
    } finally {
      clearSelection()
      await onRefresh()
    }
  }

  const updateRemarks = async (chatIds: string[], remarks: string) => {
    try {
      const result = await updateRemarksExecute({ chatIds, remarks })
      if (!isEmpty(result)) reportOutcome('conversation', 'updated (remarks)', result)
    } finally {
      clearSelection()
      await onRefresh()
    }
  }

  const replyToConversations = async (replies: ChatBatchReplyInput[]) => {
    try {
      const result = await replyExecute({ replies })
      if (!isEmpty(result)) reportOutcome('message', 'sent', result)
    } finally {
      clearSelection()
      await onRefresh()
    }
  }

  return {
    closeConversations,
    cancelConversations,
    markSpamConversations,
    assignConversations,
    updateTags,
    updateRemarks,
    replyToConversations,
    closing,
    canceling,
    markingSpam,
    assigning,
    updatingTags,
    updatingRemarks,
    replying,
  }
}
